/**
 * Retention & Trial Expiry Sequence Templates
 * Based on best practices from Netflix, Spotify, Adobe, Notion
 *
 * Key insights from billion-dollar companies:
 * - Trial expiry emails have 3-5x higher conversion than regular marketing
 * - Start 7 days before expiry, increase urgency as deadline approaches
 * - Show value received during trial (usage stats)
 * - Offer incentives strategically (last email, not first)
 * - Make cancellation easy to build trust
 */

import { SequenceTemplate } from "./types";

export const TRIAL_EXPIRING_7_DAY: SequenceTemplate = {
  id: "trial-expiring-7-day",
  name: "Trial Expiry Sequence (7-Day)",
  type: "retention",
  description: "Strategic 4-email sequence to convert trial users before expiry",
  strategy: "Follows the value-reminder-urgency-offer framework. Start by reminding of value, then create urgency, finally offer incentive. This pattern has 40%+ conversion rates.",
  icon: "ClockCircleOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "35-50% trial-to-paid conversion",
    "60%+ email open rate (high intent)",
    "Reduced involuntary churn",
    "Clear upgrade path"
  ],
  bestPractices: [
    "Show actual usage stats from their trial",
    "Calculate ROI or time saved during trial",
    "Offer discount only in final email",
    "Make the upgrade process 1-click simple",
    "Don't be pushy - provide genuine value"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 7, unit: "days" } // Assuming 14-day trial, starts 7 days before expiry
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: ["Free"],
      subscriptionStatus: ["trialing"]
    }
  },
  emails: [
    {
      name: "7 Days Left - Value Reminder",
      subject: "{{first_name}}, your trial ends in 7 days",
      suggestedTemplateId: "trial-expiring",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Gentle reminder + highlight what they've achieved",
      variables: [
        { key: "days_remaining", defaultValue: "7" },
        { key: "generations_created", defaultValue: "{{generations_created}}" },
        { key: "copilots_used", defaultValue: "{{copilots_used}}" }
      ]
    },
    {
      name: "3 Days Left - Benefits Recap",
      subject: "Don't lose access to {{product_name}}",
      suggestedTemplateId: "trial-expiring",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Recap key benefits they'll lose + comparison",
      variables: [
        { key: "days_remaining", defaultValue: "3" },
        { key: "key_benefit_1", defaultValue: "Unlimited conversations" },
        { key: "key_benefit_2", defaultValue: "Advanced analytics" }
      ]
    },
    {
      name: "1 Day Left - Urgency",
      subject: "Final day: Your AI copilot access ends tomorrow",
      suggestedTemplateId: "trial-expiring",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Create urgency with specific consequences",
      variables: [
        { key: "days_remaining", defaultValue: "1" },
        { key: "consequence", defaultValue: "Your AI copilot access will be suspended" }
      ]
    },
    {
      name: "Last Chance - Special Offer",
      subject: "{{first_name}}, we have a special offer for you",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Final push with exclusive discount",
      variables: [
        { key: "discount_amount", defaultValue: "20%" },
        { key: "promo_code", defaultValue: "STAYWITHUS" },
        { key: "offer_expiry", defaultValue: "48 hours" }
      ]
    }
  ],
  tags: ["trial", "expiry", "conversion", "retention", "urgency"],
  bestFor: ["Freemium SaaS", "Trial-based products", "Subscription services"],
  difficulty: "intermediate",
  setupTime: "20 minutes"
};

export const WIN_BACK_CHURNED: SequenceTemplate = {
  id: "win-back-churned",
  name: "Win-Back Churned Customers",
  type: "retention",
  description: "Re-engage customers who cancelled their subscription",
  strategy: "Wait 30 days after churn (cooling off period), then reach out with updates and incentives. 15-25% of churned users can be won back within 90 days.",
  icon: "HeartOutlined",
  color: "#ef4444",
  expectedOutcomes: [
    "15-25% win-back rate",
    "Lower CAC than new acquisition",
    "Valuable feedback collection",
    "Brand sentiment improvement"
  ],
  bestPractices: [
    "Wait at least 30 days - don't seem desperate",
    "Lead with product improvements, not discounts",
    "Ask for feedback to show you care",
    "Offer a 'no commitment' trial to come back",
    "Keep it personal and humble"
  ],
  trigger: {
    eventType: "inactivity",
    delay: { value: 30, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "inactive",
      engagementLevel: "low"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["canceled"]
    }
  },
  emails: [
    {
      name: "30 Days Post-Churn - We Miss You",
      subject: "{{first_name}}, we've made some changes...",
      suggestedTemplateId: "re-engagement",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Soft re-engagement + highlight new features"
    },
    {
      name: "45 Days - What's New",
      subject: "See what you're missing: New features in {{product_name}}",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 15, unit: "days" },
      purpose: "Showcase improvements since they left"
    },
    {
      name: "60 Days - Special Comeback Offer",
      subject: "Welcome back offer: 50% off your first month",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 15, unit: "days" },
      purpose: "Compelling offer to return"
    },
    {
      name: "90 Days - Final Reach Out",
      subject: "This is goodbye (unless...)",
      suggestedTemplateId: "re-engagement",
      delayAfterPrevious: { value: 30, unit: "days" },
      purpose: "Final attempt with emotional appeal"
    }
  ],
  tags: ["win-back", "churned", "re-activation", "retention"],
  bestFor: ["Subscription businesses", "SaaS with monthly plans", "Services with switching costs"],
  difficulty: "intermediate",
  setupTime: "20 minutes"
};

export const PAYMENT_FAILED_RECOVERY: SequenceTemplate = {
  id: "payment-failed-recovery",
  name: "Failed Payment Recovery",
  type: "retention",
  description: "Recover revenue from failed payments (dunning sequence)",
  strategy: "40% of involuntary churn is recoverable. Send immediate, helpful emails that make updating payment info easy. No blame, just solutions.",
  icon: "CreditCardOutlined",
  color: "#dc2626",
  expectedOutcomes: [
    "40-60% payment recovery rate",
    "Minimal service interruption",
    "Maintained customer relationship",
    "Reduced involuntary churn"
  ],
  bestPractices: [
    "Send immediately after payment failure",
    "Be helpful, not accusatory",
    "Provide multiple payment options",
    "Include direct link to update payment",
    "Offer phone support for high-value customers"
  ],
  trigger: {
    eventType: "timeAfterSignup", // Would be payment_failed in real implementation
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["past_due"]
    }
  },
  emails: [
    {
      name: "Immediate - Payment Issue",
      subject: "Action needed: Update your payment method",
      suggestedTemplateId: "pending-payment",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Alert user to payment issue + easy fix link"
    },
    {
      name: "Day 3 - Friendly Reminder",
      subject: "Reminder: Your {{product_name}} access needs attention",
      suggestedTemplateId: "pending-payment",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Gentle reminder with support offer"
    },
    {
      name: "Day 7 - Service Warning",
      subject: "Your AI copilot access ends in 3 days",
      suggestedTemplateId: "pending-payment",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Urgency + specific consequences"
    },
    {
      name: "Day 10 - Final Notice",
      subject: "Final notice: Update payment to keep your AI copilot access",
      suggestedTemplateId: "pending-payment",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Last chance before service suspension"
    }
  ],
  tags: ["payment", "dunning", "recovery", "retention", "billing"],
  bestFor: ["Subscription SaaS", "Any recurring billing", "High-value customers"],
  difficulty: "beginner",
  setupTime: "15 minutes"
};

export const RETENTION_TEMPLATES = [
  TRIAL_EXPIRING_7_DAY,
  WIN_BACK_CHURNED,
  PAYMENT_FAILED_RECOVERY
];
